import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { apiClient, createProjectHeaders, formatP0Error, isContextIntegrityError } from '../lib/api';
import { ArrowLeft, TrendingUp, Eye, Heart, MousePointer, BarChart3 } from 'lucide-react';

export default function ProjectAnalytics() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjectAndAnalytics();
  }, [projectId]);

  const fetchProjectAndAnalytics = async () => {
    // P0: Validate projectId
    if (!projectId) {
      toast.error('⚠️ PROJECT_ID_REQUIRED: Sélectionnez un projet');
      setLoading(false);
      return;
    }
    
    try {
      // P0: Add project headers
      const headers = createProjectHeaders(projectId);
      
      const [projectRes, analyticsRes] = await Promise.all([
        apiClient.get(`/projects/${projectId}`, { headers }),
        apiClient.get(`/analytics/${projectId}`, { headers })
      ]);
      
      setProject(projectRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      if (isContextIntegrityError(error)) {
        toast.error(formatP0Error(error));
      } else {
        toast.error('Erreur lors du chargement des analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num;
  };

  if (loading || !analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <header className="border-b bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold">Analytics - {project?.name}</h1>
              <p className="text-sm text-muted-foreground">Performance de vos contenus</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Contenus créés</p>
                  <p className="text-3xl font-bold font-heading">{analytics.total_contents}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Publiés</p>
                  <p className="text-3xl font-bold font-heading">{analytics.published_contents}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Impressions</p>
                  <p className="text-3xl font-bold font-heading">{formatNumber(analytics.total_impressions)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-warning" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Engagement</p>
                  <p className="text-3xl font-bold font-heading">{analytics.avg_engagement_rate}%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-error" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Performance par plateforme</CardTitle>
            <CardDescription>Analyse des résultats par canal</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(analytics.by_platform || {}).map(([platform, data]) => (
                <div key={platform} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium capitalize">{platform}</p>
                    <p className="text-sm text-muted-foreground">{data.count} contenus</p>
                  </div>
                  <div className="flex gap-8 text-right">
                    <div>
                      <p className="text-sm text-muted-foreground">Impressions</p>
                      <p className="font-semibold">{formatNumber(data.impressions)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Engagements</p>
                      <p className="font-semibold">{formatNumber(data.engagements)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Taux</p>
                      <p className="font-semibold">
                        {data.impressions > 0 
                          ? ((data.engagements / data.impressions) * 100).toFixed(2) 
                          : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Contenus les plus performants</CardTitle>
            <CardDescription>Classés par taux d'engagement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.top_performing?.slice(0, 5).map((content, idx) => (
                <div key={content.content_id} className="flex items-start gap-4 p-4 border rounded-lg">
                  <Badge className="mt-1">{idx + 1}</Badge>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{content.text}</p>
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="capitalize">{content.platform}</span>
                      <span>•</span>
                      <span>{formatNumber(content.impressions)} vues</span>
                      <span>•</span>
                      <span>{formatNumber(content.engagements)} engagements</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-success border-success/30">
                    {content.engagement_rate.toFixed(2)}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
